import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Instagram } from 'lucide-react';
import { siteConfig } from '../../config/site';

const Footer: React.FC = () => {
  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative py-16 lg:py-20 overflow-hidden bg-slate-100 border-t border-slate-200">
      {/* Logo Imprint Background - Positioned higher */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2 pointer-events-none">
        <img
          src="/insurance logos/EC logo transparent main.png"
          alt=""
          className="w-[400px] h-auto opacity-[0.06]"
        />
      </div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        {/* Top Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <img
              src="/insurance logos/EC logo transparent main.png"
              alt="Enhance Companies"
              className="h-14 w-auto mb-4"
            />
            <p className="text-slate-500 text-sm leading-relaxed">
              Helping agents and agencies grow since 2022.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-slate-900 mb-4 text-sm uppercase tracking-wider">Company</h3>
            <ul className="space-y-3">
              {[
                { to: '/', label: 'Home' },
                { to: '/about', label: 'About' },
                { to: '/carriers', label: 'Carriers' },
                { to: '/technology', label: 'Technology' },
              ].map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to}
                    onClick={handleLinkClick}
                    className="text-slate-500 hover:text-slate-900 transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="font-semibold text-slate-900 mb-4 text-sm uppercase tracking-wider">Resources</h3>
            <ul className="space-y-3">
              {[
                { to: '/store', label: 'Shop' },
                { to: '/lead-catalog', label: 'Lead Catalog' },
                { to: '/agent/login', label: 'Agent Login' },
                { to: '/apply', label: 'Apply Now' },
                { to: '/for-agencies', label: 'For Agencies' },
              ].map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to}
                    onClick={handleLinkClick}
                    className="text-slate-500 hover:text-slate-900 transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-slate-900 mb-4 text-sm uppercase tracking-wider">Contact</h3>
            <div className="space-y-3">
              <a
                href={`tel:${siteConfig.contact.phone}`}
                className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors text-sm"
              >
                <Phone className="h-4 w-4" />
                <span>{siteConfig.contact.phone}</span>
              </a>
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <MapPin className="h-4 w-4" />
                <span>Los Angeles, CA</span>
              </div>
              <a 
                href="https://www.instagram.com/enhancecompanies/?hl=en" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors text-sm mt-2"
              >
                <Instagram className="h-4 w-4" />
                <span>@enhancecompanies</span>
              </a>
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="mb-12">
          <div className="bg-white rounded-xl overflow-hidden border border-slate-200 shadow-sm">
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-slate-400" />
                <span className="text-sm text-slate-700 font-medium">Headquarters</span>
                <span className="text-sm text-slate-400">• Los Angeles, California</span>
              </div>
            </div>
            <div className="relative h-48 bg-slate-50">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d423286.27404345275!2d-118.69191921441498!3d34.02016130939498!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2c75ddc27da13%3A0xe22fdf6f254608f4!2sLos%20Angeles%2C%20CA!5e0!3m2!1sen!2sus!4v1704067200000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'grayscale(100%) contrast(95%)' }}
                allowFullScreen={false}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Enhance Companies Location"
                className="absolute inset-0"
              />
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-200 pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} SW Capital LLC, DBA {siteConfig.name}. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm">
              <Link 
                to="/contact" 
                onClick={handleLinkClick} 
                className="text-slate-400 hover:text-slate-700 transition-colors"
              >
                Contact
              </Link>
              <Link 
                to="/apply" 
                onClick={handleLinkClick} 
                className="text-slate-400 hover:text-slate-700 transition-colors"
              >
                Join Us
              </Link>
              <Link 
                to="/privacy-policy" 
                onClick={handleLinkClick} 
                className="text-slate-400 hover:text-slate-700 transition-colors"
              >
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
